/**
 * Signs the user out after a stretch of inactivity and sends them to the
 * login page.
 *
 * The limit is the school's setting (Settings → Profile → Session security),
 * else the platform's 30 minutes; it arrives on the sign-in payload as
 * `user.idleTimeoutMinutes`. A parent gets null, and no timer.
 *
 * - Activity is the user's own input: pointer, keys, wheel, touch, scroll.
 *   Background polling is not activity — it is what used to keep an untouched
 *   tab signed in forever.
 * - The last-activity time is shared across tabs through localStorage, so
 *   working in one tab keeps the others alive, and they all expire together.
 * - The check runs on a timer and again whenever the tab becomes visible,
 *   because a sleeping laptop runs no timers.
 * - While the user is active the session is renewed at half the limit. The
 *   server refuses a refresh whose last renewal is older than the limit
 *   (`session_idle`), which covers the closed tab that never ran this code.
 *
 * Mounted once, from App.vue.
 */
import { onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

export const LAST_ACTIVITY_KEY = 'kori.lastActivityAt'

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const
const CHECK_EVERY_MS = 15_000
// Writing localStorage on every mousemove is wasteful; a few seconds of
// imprecision against a limit measured in minutes is not.
const RECORD_AT_MOST_EVERY_MS = 5_000

function readShared(): number | null {
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVITY_KEY)
    const at = raw === null ? NaN : Number(raw)
    return Number.isFinite(at) ? at : null
  } catch {
    return null
  }
}

function writeShared(at: number): void {
  try {
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at))
  } catch {
    // Private mode or blocked storage: this tab still keeps its own time.
  }
}

/** True when `lastActivityAt` is at least `minutes` before `now`. */
export function hasBeenIdle(lastActivityAt: number, now: number, minutes: number): boolean {
  return now - lastActivityAt >= minutes * 60_000
}

export function useIdleSignOut(): void {
  const auth = useAuthStore()
  const router = useRouter()

  let lastActivityAt = 0
  let lastRecordedAt = 0
  let timer: ReturnType<typeof setInterval> | null = null
  let expiring = false

  const limitMinutes = (): number | null => {
    const m = auth.user?.idleTimeoutMinutes
    return typeof m === 'number' && m > 0 ? m : null
  }

  function onActivity(): void {
    const now = Date.now()
    lastActivityAt = now
    if (now - lastRecordedAt >= RECORD_AT_MOST_EVERY_MS) {
      lastRecordedAt = now
      writeShared(now)
    }
    const limit = limitMinutes()
    if (limit !== null && auth.isAuthenticated && now - auth.lastRenewedAt >= (limit * 60_000) / 2) {
      // Renew so the server sees an active session; refresh() is single-flight.
      auth.lastRenewedAt = now
      void auth.refresh()
    }
  }

  function check(): void {
    const limit = limitMinutes()
    if (limit === null || !auth.isAuthenticated || expiring) return
    const last = Math.max(lastActivityAt, readShared() ?? 0)
    if (hasBeenIdle(last, Date.now(), limit)) void expire()
  }

  function onVisible(): void {
    if (document.visibilityState === 'visible') check()
  }

  async function expire(): Promise<void> {
    expiring = true
    stop()
    await auth.logout({ reason: 'idle' })
    expiring = false
    // The watcher below sends the user to the login page.
  }

  function start(): void {
    if (timer !== null) return
    // Signing in, or reloading a live session, is activity.
    onActivity()
    for (const e of ACTIVITY_EVENTS) {
      window.addEventListener(e, onActivity, { capture: true, passive: true })
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', check)
    timer = setInterval(check, CHECK_EVERY_MS)
  }

  function stop(): void {
    if (timer === null) return
    for (const e of ACTIVITY_EVENTS) {
      window.removeEventListener(e, onActivity, { capture: true })
    }
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('focus', check)
    clearInterval(timer)
    timer = null
  }

  watch(
    () => [auth.isAuthenticated, limitMinutes()] as const,
    ([signedIn, limit]) => {
      if (signedIn && limit !== null) start()
      else stop()
    },
    { immediate: true },
  )

  // However the session ended without the user asking — this timer, or the
  // server refusing an idle refresh — leave the page they can no longer use.
  // A deliberate sign-out has no reason and does its own navigation.
  watch(
    () => auth.isAuthenticated,
    (signedIn, wasSignedIn) => {
      if (signedIn || !wasSignedIn || auth.signOutReason === null) return
      const current = router.currentRoute.value
      if (current.meta.public) return
      void router.replace({
        name: 'auth.login',
        query: { reason: auth.signOutReason, next: current.fullPath },
      })
    },
  )

  onBeforeUnmount(stop)
}
